import { parsePhoneNumberFromString } from 'libphonenumber-js';
import type { IntlShape } from 'react-intl';
import type { InferType } from 'yup';
import { bool, object, string } from 'yup';

import type { Country } from '@/types/regionTypes';

import { MAX_CHAR_PHONE } from '@/lib/forms/const';
import { getNameSchema, getTitleSchema } from '@/lib/forms/schemas';
import { getPhonePrefixSelectOptByCode } from '@/lib/phonePrefixes/helpers';
import { commonMessages as appointmentMessages } from '@/messages/appointment';
import { formMessages } from '@/messages/forms';
import type { PhonePrefix } from '@/views/DeliveryView/helpers';

export const appointmentFormValidFields: readonly (keyof AppointmentFormValues)[] =
  [
    'title',
    'newsletterOptIn',
    'firstName',
    'lastName',
    'firstNameKanji',
    'lastNameKanji',
    'email',
    'phonePrefix',
    'phone',
    'reason',
    'timeSlot',
    'region',
    'city',
    'retailer',
    'comments',
    'consent',
    'captcha',
  ] as const;

export type AppointmentFormValues = InferType<
  ReturnType<typeof appointmentSchema>
>;

export const appointmentSchema = (
  formatMessage: IntlShape['formatMessage'],
  country: Country,
  isJapan: boolean,
  doorLocations: { label: string; value: string }[],
) => {
  return object({
    title: getTitleSchema(formatMessage, country.code),
    newsletterOptIn: bool().default(false),
    firstName: getNameSchema(formatMessage, 'first'),
    lastName: getNameSchema(formatMessage, 'last'),
    ...(isJapan && {
      firstNameKanji: getNameSchema(formatMessage, 'firstKanji'),
      lastNameKanji: getNameSchema(formatMessage, 'lastKanji'),
    }),
    email: string()
      .email(formatMessage(appointmentMessages.invalidEmail))
      .required(formatMessage(appointmentMessages.requireEmail))
      .default(''),
    phonePrefix: object({
      value: string(),
      code: string(),
      label: string(),
    }).default(getPhonePrefixSelectOptByCode(country.code)),
    phone: string()
      .required(formatMessage(appointmentMessages.requirePhone))
      .max(MAX_CHAR_PHONE, formatMessage(appointmentMessages.maxPhoneCharMsg))
      .when('phonePrefix', {
        is: (phonePrefix: PhonePrefix) => !!phonePrefix?.value,
        then: (schema) =>
          schema.test(
            'is-valid-phone',
            formatMessage(formMessages.invalidPhoneNumber),
            function (value) {
              const { phonePrefix } = this.parent;

              if (!phonePrefix.value) {
                return false;
              }

              const fullPhoneNumber = `${phonePrefix.value}${value}`;

              const phoneNumber = parsePhoneNumberFromString(fullPhoneNumber);
              if (!phoneNumber) {
                return false;
              }

              const isValid = phoneNumber.isValid();
              return isValid;
            },
          ),
      })
      .default(''),
    reason: object().default({
      label: formatMessage(appointmentMessages.discover_and_try),
      value: 'discover_and_try',
    }),
    timeSlot: object().required(
      formatMessage(appointmentMessages.requireField),
    ),
    ...(doorLocations.length > 0 && {
      locationId: object().required(
        formatMessage(appointmentMessages.requireField),
      ),
    }),
    region: object(),
    city: object().nullable(),
    retailer: object().nullable(),
    comments: string()
      .max(3000, formatMessage(appointmentMessages.maxCommentsMsg))
      .default(''),
    consent: bool()
      .default(false)
      .oneOf([true], formatMessage(formMessages.requiredField)),
    captcha: bool()
      .default(false)
      .oneOf([true], formatMessage(appointmentMessages.requireField)),
  });
};
